/// <amd-module name="Build/Common/Library/PipelineService" />
import { BuildDefinition } from "Build/Client/Contracts/Build";
import {
    Build, BuildDefinitionVariable, ContinuousIntegrationTrigger, DefinitionQueueStatus, DefinitionTriggerType
} from "Build/Client/Contracts/Build";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import * as LibraryResources from "Build/Common/Library/Resources";
import { RepositoryDescriptor } from "Build/Common/Library/Types";
import { SaveResult } from "Build/Common/Library/Designer.types";
import { CreateAndRunPipelineRequestParams } from "Pipelines/Common/Library/Contracts/DataProvider";

import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

export interface PipelineInfo {
    id: number;
    name: string;
    queueName: string;
}

export interface CreateAndRunPipelineResult {
    pipeline: PipelineInfo;
    pipelineBuildWebUrl: string;
    queuePipelineErrorMessage: string;
}

export interface IPipelineService extends IVssService {

    createAndRunPipeline(
        repository: RepositoryDescriptor,
        path: string,
        sourceBranch?: string,
        saveResult?: SaveResult,
        pipelinePath?: string,
        variables?: { [key: string]: BuildDefinitionVariable },
        createOnly?: boolean,
        defaultQueueName?: string): Promise<CreateAndRunPipelineResult | undefined>;

    runPipeline(definition: BuildDefinition, branch: string): Promise<Build>;

    /**
     * Enable, disable, or pause the pipeline by updating the queue status.
     */
    enablePipeline(definition: BuildDefinition, queueStatus: DefinitionQueueStatus): Promise<BuildDefinition>;

    /**
     * Enable/Disable pipeline CI (continuous integration trigger settings).
     * @param enable - If true then the pipeline CI will default to all branches and honor YAML trigger settings.
     *               - If false then all CI settings are removed from the pipeline and it ignores YAML triggers.
     */
    enablePipelineCI(definition: BuildDefinition, enable: boolean): Promise<BuildDefinition>;

    /**
     * Save the given pipeline.
     */
    savePipeline(definition: BuildDefinition): Promise<BuildDefinition>;

    /**
     * retrieve any errors from the data provider
     */
    getPipelineErrorMessage(): string;
}

class PipelineService extends VssService implements IPipelineService {
    private static readonly _pipelineDataProviderId: string = "ms.vss-build-web.create-and-run-pipeline-data-provider";

    public createAndRunPipeline(
        repository: RepositoryDescriptor,
        path: string,
        sourceBranch?: string,
        saveResult?: SaveResult,
        pipelinePath?: string,
        variables?: { [key: string]: BuildDefinitionVariable },
        createOnly?: boolean,
        defaultQueueName?: string): Promise<CreateAndRunPipelineResult | undefined> {

        // TODO: if this.state.pipelineCreated exists and we are here, it means we have failed to queue the build but the pipeline
        // is created prior.  Should we skip creating pipeline and just queue the build with a rest client?
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        let dataInputs: Partial<CreateAndRunPipelineRequestParams> = {
            createOnly: createOnly,
            connectionId: repository.connectionId,
            sourceProvider: repository.sourceProviderType,
            pipelinePath: pipelinePath,
            repositoryId: repository.id,
            repositoryName: repository.name,
            branch: repository.defaultBranch, // branch used to create the pipeline (repository default branch)
            sourceBranch: sourceBranch || repository.defaultBranch, // branch used to queue the build (different from 'branch' in case of pull request)
            filePath: path,
            queue: defaultQueueName,
        };

        if (saveResult) {
            dataInputs = {
                ...dataInputs,
                commitId: saveResult.commitId,
                pullRequestNumber: saveResult.pullRequestNumber,
                commitDescriptorName: saveResult.commitDescriptorName,
            }
        }

        if (variables && Object.keys(variables).length > 0) {
            dataInputs.variables = variables;
        }

        return contributionService.getDataAsync<CreateAndRunPipelineResult>(PipelineService._pipelineDataProviderId, dataInputs, true);
    }

    public runPipeline(definition: BuildDefinition, branch: string): Promise<Build> {
        const client = this.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
        const build = {
            definition: { id: definition.id },
            sourceBranch: branch,
        } as Build;

        return client.queueBuild(build, definition.project.id);
    }

    public enablePipelineCI(definition: BuildDefinition, enable: boolean): Promise<BuildDefinition> {
        definition = this.shallowCopy(definition);

        // Remove existing pipeline CI Trigger settings
        if (definition.triggers) {
            definition.triggers = definition.triggers.filter(trigger =>
                trigger.triggerType !== DefinitionTriggerType.ContinuousIntegration);
        }

        // If enabling, add default YAML CI Trigger settings
        if (enable) {
            const yamlCITrigger: ContinuousIntegrationTrigger = {
                batchChanges: false,
                maxConcurrentBuildsPerBranch: 1,
                pollingJobId: "",
                pollingInterval: 0,
                pathFilters: [],
                branchFilters: [],
                settingsSourceType: 2 /* SettingsSourceType.Process */,
                triggerType: DefinitionTriggerType.ContinuousIntegration
            }
            definition.triggers = definition.triggers || [];
            definition.triggers.push(yamlCITrigger);
            definition.comment = LibraryResources.SavePipelineCommentEnableCI;
        }

        definition.comment = enable ? LibraryResources.SavePipelineCommentEnableCI : LibraryResources.SavePipelineCommentDisableCI;
        return this.savePipeline(definition);
    }

    public enablePipeline(definition: BuildDefinition, queueStatus: DefinitionQueueStatus): Promise<BuildDefinition> {
        definition = this.shallowCopy(definition);

        definition.queueStatus = queueStatus;

        switch (queueStatus) {
            case DefinitionQueueStatus.Disabled:
                definition.comment = LibraryResources.SavePipelineCommentDisable;
                break;
            case DefinitionQueueStatus.Enabled:
                definition.comment = LibraryResources.SavePipelineCommentEnable;
                break;
            case DefinitionQueueStatus.Paused:
                definition.comment = LibraryResources.SavePipelineCommentPause;
                break;
        }

        return this.savePipeline(definition);
    }

    public savePipeline(definition: BuildDefinition): Promise<BuildDefinition> {
        const client = this.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
        return client.updateDefinition(definition, definition.project.id, definition.id);
    }

    public getPipelineErrorMessage(): string {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const exception = contributionService.getDataProviderExceptions()[PipelineService._pipelineDataProviderId];
        return exception && exception.message;
    }

    private deepCopy<T>(object: T): T {
        return JSON.parse(JSON.stringify(object));
    }

    private shallowCopy<T extends object>(t: T): T {
        return { ...(t as object) } as T;
    }
}

Services.add("IPipelineService", { serviceFactory: PipelineService });
